import { Table, TableBody, Button } from '@/components/ui';
import { DataTablePagination, PanelInfo } from '@/components/custom';
import { broadcastService } from '@/services';
import { useUserConnected } from '@/hooks';
import { useRequestUpdatePromotionStatus, useRequestDeletePromotion } from '@/hooks/httpRequest/promotions';
import type { PromotionStatusEnum } from '@/enums';
import { notify } from '@/functions';
import { Megaphone, AlertTriangle } from 'lucide-react';
import type { IPromotion } from '@/interfaces';
import DataTableHeader from './DataTableHeader';
import DataTableBodySkeleton from './DataTableBodySkeleton';
import DataTableMobileSkeleton from './DataTableMobileSkeleton';
import { DataTableDesktopRow, DataTableMobileCard } from './components';
import type usePromotions from '../../hooks/usePromotions';

type TProps = {
	data: ReturnType<typeof usePromotions>;
	onViewDetails: (promotion: IPromotion) => void;
};

const DataTable = ({ data, onViewDetails }: TProps) => {
	const { promotions, isLoading } = data;
	const { userConnected } = useUserConnected();
	const isRoot = !!userConnected?.root;

	const { triggerUpdatePromotionStatus } = useRequestUpdatePromotionStatus({
		onSuccess: () => { notify('Statut mis à jour.'); },
	});

	const { triggerDeletePromotion } = useRequestDeletePromotion({
		onSuccess: () => { notify('Promotion supprimée.'); },
	});

	const handleSetStatus = async (promotion: IPromotion, status: PromotionStatusEnum) => {
		try {
			await triggerUpdatePromotionStatus({ id: promotion._id, status });
		} catch {
			notify('Erreur lors de la mise à jour du statut.', 'error');
		}
	};

	const handleDeleteClick = (promotion: IPromotion) => {
		broadcastService.modal.show({
			content: (
				<div className="flex flex-col items-center text-center space-y-4 py-2">
					<div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
						<AlertTriangle className="h-6 w-6 text-destructive" />
					</div>
					<div className="space-y-1">
						<p className="font-medium text-foreground">Supprimer la promotion ?</p>
						<p className="text-xs text-muted-foreground">Cette action est irréversible.</p>
					</div>
					<div className="flex space-x-2 pt-2 w-full">
						<Button variant="outline" onClick={() => broadcastService.modal.hide()} size="sm" className="flex-1">
							Annuler
						</Button>
						<Button
							variant="destructive"
							onClick={async () => {
								broadcastService.modal.hide();
								try { await triggerDeletePromotion(promotion._id); }
								catch { notify('Erreur lors de la suppression.', 'error'); }
							}}
							size="sm"
							className="flex-1"
						>
							Supprimer
						</Button>
					</div>
				</div>
			),
			width: 'xs',
			showCloseIcon: false,
		});
	};

	if (isLoading) {
		return (
			<div className="space-y-5">
				<div className="hidden md:block border rounded-md">
					<Table>
						<DataTableHeader />
						<DataTableBodySkeleton rows={8} />
					</Table>
				</div>
				<div className="block md:hidden">
					<DataTableMobileSkeleton count={5} />
				</div>
			</div>
		);
	}

	if (promotions.length === 0) {
		return (
			<PanelInfo
				title="Aucune promotion"
				description="Aucune promotion ne correspond aux critères sélectionnés."
				className="py-10 md:py-26 border-none"
				icon={Megaphone}
			/>
		);
	}

	return (
		<div className="space-y-5">
			<div className="hidden md:block border rounded-md">
				<Table>
					<DataTableHeader />
					<TableBody>
						{promotions.map(promo => (
							<DataTableDesktopRow
								key={promo._id}
								promotion={promo}
								onSetStatus={handleSetStatus}
								onDelete={handleDeleteClick}
								onViewDetails={onViewDetails}
								canDelete={isRoot}
							/>
						))}
					</TableBody>
				</Table>
			</div>
			<div className="block md:hidden">
				<div className="space-y-4">
					{promotions.map(promo => (
						<DataTableMobileCard
							key={promo._id}
							promotion={promo}
							onSetStatus={handleSetStatus}
							onDelete={handleDeleteClick}
							onViewDetails={onViewDetails}
							canDelete={isRoot}
						/>
					))}
				</div>
			</div>

			{data.data?.pagination && <DataTablePagination pageCount={data.data.pagination.pageCount} />}
		</div>
	);
};

export default DataTable;
